<script lang="ts" module>
  import type { AiSuggestion } from '@/teoCore/TeoApi';
  import BaseBtn from '@/ui/base/BaseBtn.svelte';
  import Icon from '@/ui/base/icon/Icon.svelte';
  import AIContentHeader from '@/ui/components/assistant/Performance/AICard/cmps/AIContentHeader.svelte';
  import { useActiveText } from '@/utils/teo-context';

  import { generateDiff } from './generate-diff';

  namespace AISuggestions {
    export interface Props {
      suggestions: AiSuggestion[];
      finalStateMessage?: string;
      loading?: boolean;
      onSuggestionUsed?: (aiSuggestion: AiSuggestion) => void;
      onRemoveSuggestion: (index: number) => void;
    }
  }
</script>

<script lang="ts">
  import LoadingArea from '@/ui/base/LoadingArea.svelte';

  let teoText = useActiveText();
  let { suggestions, onRemoveSuggestion, ...props }: AISuggestions.Props = $props();

  // Calculates the index of the shown suggsestion
  let currentPage = $state(0);
  $effect(() => {
    currentPage = Math.max((suggestions?.length ?? 0) - 1, 0);
  });

  let showText = $derived.by(() => {
    if (suggestions.length === 0) {
      return props.finalStateMessage ?? '';
    }
    return generateDiff(
      suggestions[currentPage]?.context.text ?? '',
      suggestions[currentPage]?.text ?? '',
    );
  });

  function onInsertClick(aiSuggestion?: AiSuggestion): void {
    if (!aiSuggestion) {
      console.warn('No AI suggestion provided');
      return;
    }
    teoText.current.ai.useSuggestion({
      markerId: aiSuggestion.context.replacementWrapper,
      replacement: aiSuggestion.text,
    });
    props.onSuggestionUsed?.(aiSuggestion);
  }
</script>

<AIContentHeader pages={suggestions?.length ?? 0} bind:current={currentPage}></AIContentHeader>
{#if props.loading}
  <div class="flex h-20 overflow-hidden rounded-sm">
    <LoadingArea />
  </div>
{:else if suggestions?.length}
  <div class="relative rounded-sm bg-sec py-3 pl-3 pr-6 hover:bg-sec-H">
    {#if props.loading}
      <LoadingArea />
    {:else}
      <div class="font-sans text-sm font-normal text-c-base">
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html showText}
      </div>
      {#if suggestions.length > 1}
        <BaseBtn class="absolute right-1 top-1" onclick={() => onRemoveSuggestion(currentPage)}>
          <Icon icon="Close" class="size-4 text-c-light"></Icon>
        </BaseBtn>
      {/if}
      {#if suggestions.length > 0}
        <BaseBtn
          class="absolute bottom-1 right-1"
          onclick={() => {
            onInsertClick?.(suggestions[currentPage]);
          }}
        >
          <Icon icon="Transfer" class="size-4 text-c-link"></Icon>
        </BaseBtn>
      {/if}
    {/if}
  </div>
{/if}
